import {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  CreditCard,
  Hash,
  Home
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { getItem, setItem } from '../utils/storage';

type StatusType = 'SUCCESS' | 'FAILURE' | 'PENDING' | 'CANCELLED';

function TransactionSummary() {
  const getUrlParams = () => {
    if (typeof window !== 'undefined') {
      const urlSearchParams = new URLSearchParams(window.location.search);
      return {
        orderId: urlSearchParams.get('orderId') || 'N/A',
        transactionId: urlSearchParams.get('transactionId') || 'N/A',
        amount: urlSearchParams.get('amount') || '0',
        status: urlSearchParams.get('status')?.toUpperCase() || 'PENDING'
      };
    }
    // Fallback for server-side rendering or testing
    return {
      orderId: '1234',
      transactionId: '5678',
      amount: '9090',
      status: 'SUCCESS'
    };
  };

  const urlParams = getUrlParams();
  const token = getItem('token');
  const navigate = useNavigate();

  const statusConfig = {
    SUCCESS: {
      icon: CheckCircle,
      title: 'Payment Successful',
      subtitle: 'Your transaction has been completed successfully',
      bgGradient: 'from-green-500 to-emerald-600',
      bgPattern: 'from-green-50 via-white to-emerald-50',
      cardBorder: 'border-green-100',
      statusBg: 'bg-green-100',
      statusText: 'text-green-800',
      messageBg: 'bg-green-50 border-green-200',
      messageTitle: 'text-green-800',
      messageText: 'text-green-700',
      messageContent:
        'Your payment has been processed successfully. You will receive a confirmation email shortly with your transaction details.'
    },
    FAILURE: {
      icon: XCircle,
      title: 'Payment Failed',
      subtitle: 'Your transaction could not be completed',
      bgGradient: 'from-red-500 to-red-600',
      bgPattern: 'from-red-50 via-white to-orange-50',
      cardBorder: 'border-red-100',
      statusBg: 'bg-red-100',
      statusText: 'text-red-800',
      messageBg: 'bg-red-50 border-red-200',
      messageTitle: 'text-red-800',
      messageText: 'text-red-700',
      messageContent:
        'Your payment could not be processed. This might be due to insufficient funds, network issues, or bank restrictions. Please try again or contact your bank.'
    },
    PENDING: {
      icon: Clock,
      title: 'Payment Pending',
      subtitle: 'Your transaction is being processed',
      bgGradient: 'from-yellow-500 to-orange-600',
      bgPattern: 'from-yellow-50 via-white to-orange-50',
      cardBorder: 'border-yellow-100',
      statusBg: 'bg-yellow-100',
      statusText: 'text-yellow-800',
      messageBg: 'bg-yellow-50 border-yellow-200',
      messageTitle: 'text-yellow-800',
      messageText: 'text-yellow-700',
      messageContent:
        'Your payment is currently being processed. This may take a few minutes. You will be notified once the transaction is complete.'
    },
    CANCELLED: {
      icon: AlertCircle,
      title: 'Payment Cancelled',
      subtitle: 'Your transaction has been cancelled',
      bgGradient: 'from-gray-500 to-gray-600',
      bgPattern: 'from-gray-50 via-white to-slate-50',
      cardBorder: 'border-gray-100',
      statusBg: 'bg-gray-100',
      statusText: 'text-gray-800',
      messageBg: 'bg-gray-50 border-gray-200',
      messageTitle: 'text-gray-800',
      messageText: 'text-gray-700',
      messageContent:
        'Your payment has been cancelled. No amount has been charged from your account. You can retry the payment if needed.'
    }
  };

  const currentStatus =
    statusConfig[urlParams.status as StatusType] || statusConfig.FAILURE;
  const StatusIcon = currentStatus.icon;

  const formatAmount = (amount: string) => {
    const numericAmount = parseInt(amount) || 0;
    return `₹${numericAmount.toLocaleString('en-IN')}`;
  };

  const handleGoHome = () => {
    if (token) {
      setItem({ key: 'activeItem', value: 'Dashboard' });
      navigate('/analytics');
      setItem({ key: 'shouldRelode', value: 'true' });
    } else {
      navigate('/');
    }
  };

  return (
    <div
      className={`min-h-screen bg-gradient-to-br ${currentStatus.bgPattern} flex items-center justify-center p-4`}
    >
      <div className='w-full max-w-lg'>
        <div
          className={`overflow-hidden rounded-3xl border bg-white shadow-2xl ${currentStatus.cardBorder}`}
        >
          <div
            className={`bg-gradient-to-r ${currentStatus.bgGradient} relative overflow-hidden px-8 py-6 text-center`}
          >
            <div className='absolute inset-0 -skew-y-6 scale-150 transform bg-white opacity-10'></div>
            <div className='relative z-10'>
              <div className='mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white bg-opacity-20 backdrop-blur-sm'>
                <StatusIcon className='h-12 w-12 text-white' />
              </div>
              <h1 className='mb-2 text-2xl font-bold text-white'>
                {currentStatus.title}
              </h1>
              <p className='text-sm text-white text-opacity-90'>
                {currentStatus.subtitle}
              </p>
            </div>
          </div>

          <div className='px-8 py-6'>
            <div className='mb-6 text-center'>
              <div className='mb-1 text-3xl font-bold text-gray-800'>
                {formatAmount(urlParams.amount)}
              </div>
              <div className='text-sm text-gray-500'>Transaction Amount</div>
            </div>

            <div className='mb-6 space-y-4'>
              <div className='space-y-3 rounded-2xl bg-gray-50 p-4'>
                <h3 className='mb-3 flex items-center text-lg font-semibold text-gray-800'>
                  <CreditCard className='mr-2 h-5 w-5' />
                  Transaction Details
                </h3>

                <div className='space-y-3'>
                  <div className='flex items-center justify-between'>
                    <span className='flex items-center text-sm text-gray-600'>
                      <Hash className='mr-2 h-4 w-4' />
                      Order ID
                    </span>
                    <span className='font-mono text-sm font-medium text-gray-800'>
                      #{urlParams.orderId}
                    </span>
                  </div>

                  <div className='flex items-center justify-between'>
                    <span className='flex items-center text-sm text-gray-600'>
                      <Hash className='mr-2 h-4 w-4' />
                      Transaction ID
                    </span>
                    <span className='font-mono text-sm font-medium text-gray-800'>
                      {urlParams.transactionId}
                    </span>
                  </div>

                  {/* <div className='flex items-center justify-between'>
                    <span className='flex items-center text-sm text-gray-600'>
                      <Calendar className='mr-2 h-4 w-4' />
                      Date & Time
                    </span>
                    <span className='text-sm font-medium text-gray-800'>
                      {formatDate()}
                    </span>
                  </div> */}

                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-gray-600'>Status</span>
                    <span
                      className={`px-3 py-1 ${currentStatus.statusBg} ${currentStatus.statusText} rounded-full text-xs font-semibold`}
                    >
                      {urlParams.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`${currentStatus.messageBg} border ${currentStatus.messageBg.includes('border') ? '' : 'border-gray-200'} mb-6 rounded-2xl p-4`}
            >
              <h4
                className={`font-semibold ${currentStatus.messageTitle} mb-2`}
              >
                {urlParams.status === 'SUCCESS'
                  ? 'Transaction Complete!'
                  : urlParams.status === 'PENDING'
                    ? 'Processing...'
                    : urlParams.status === 'CANCELLED'
                      ? 'Payment Cancelled'
                      : 'What went wrong?'}
              </h4>
              <p
                className={`${currentStatus.messageText} text-sm leading-relaxed`}
              >
                {currentStatus.messageContent}
              </p>
            </div>

            {/* Action Buttons */}
            <div className='space-y-3'>
              {/* <button
                onClick={handlePrimaryAction}
                className={`w-full ${
                  urlParams.status === 'SUCCESS'
                    ? 'bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800'
                    : urlParams.status === 'PENDING'
                      ? 'bg-gradient-to-r from-yellow-600 to-orange-700 hover:from-yellow-700 hover:to-orange-800'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                } flex transform items-center justify-center rounded-2xl px-6 py-4 font-semibold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl`}
              >
                <PrimaryIcon className='mr-2 h-5 w-5' />
                {getPrimaryButtonText()}
              </button> */}

              <div className='mx-auto flex w-full justify-center'>
                <button
                  onClick={handleGoHome}
                  className='flex items-center justify-center rounded-xl bg-gray-100 px-4 py-3 font-medium text-gray-700 transition-all duration-200 hover:bg-gray-200'
                >
                  <Home className='mr-2 h-4 w-4' />
                  Go Home
                </button>

                {/* <button
                  onClick={handleContactSupport}
                  className='rounded-xl bg-orange-100 px-4 py-3 text-sm font-medium text-orange-700 transition-all duration-200 hover:bg-orange-200'
                >
                  Contact Support
                </button> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TransactionSummary;
